<script lang="ts">
  import { trim } from "lodash";
  import { createEventDispatcher, onMount } from "svelte";
  import { api } from "../api/client";
  import type {
    Buyer,
    DatabaseMatch,
    InvoiceLine,
    Recipe,
    RecipeLineMatch,
  } from "../helpers/types";
  import ButtonSmall from "./ButtonSmall.svelte";
  import LoadingSpinner from "./LoadingSpinner.svelte";
  import RecipeIngredientLink from "./RecipeIngredientLink.svelte";
  import { stratify } from "d3-hierarchy";

  export let buyer: Buyer | null;
  export let recipes: Recipe[];
  export let searchIndex: Map<string, Recipe> = new Map();
  export let allIngredientToRecipes: Record<string, Recipe[]> = {};

  let selectedRecipeIds: string[] = [];
  let connectedLines: RecipeLineMatch[] = [];
  let connectedLinesManual: RecipeLineMatch[] = [];
  let allInvoiceLines: InvoiceLine[] = [];

  let recipeSold: Record<string, number> = {};
  let recipeFlatIngredients: Record<string, Recipe>;

  let ingredientToRecipes: Record<string, string[]> = {};
  let linkingRecipesToInvoices: boolean = false;

  let savingLinks: boolean = false;

  const dispatch = createEventDispatcher();

  function collectIngredients(recipe: Recipe, parentId = "") {
    let flatRecipe: Recipe[] = [];
    if (recipe.items === undefined) return flatRecipe;

    for (const ingredient of recipe.items) {
      if (!ingredient.id) continue;
      let mainRecipeId = parentId ? parentId : recipe.id;
      if (!mainRecipeId) {
        throw new Error("no recipe id");
      }
      if (ingredient.type === "recipe") {
        recipeSold[ingredient.id] = recipeSold[parentId] * (recipe.usage || 1);
        let subRecipe = collectIngredients(ingredient, mainRecipeId);
        if (subRecipe) {
          flatRecipe.push({
            ...ingredient,
            items: subRecipe,
            type: "recipe",
            quantity: recipeSold[mainRecipeId] * (ingredient.usage ?? 0),
          });
        }
      } else {
        if (!ingredientToRecipes[ingredient.id]) ingredientToRecipes[ingredient.id] = [];

        ingredientToRecipes[ingredient.id].push(mainRecipeId);
        flatRecipe.push({
          ...ingredient,
          type: "ingredient",
          quantity:
            recipeSold[mainRecipeId] * (recipe.usage ?? 0)
              ? (recipe.usage ?? 0) * (ingredient.usage ?? 0)
              : ingredient.usage,
        });
      }
    }
    return flatRecipe;
  }

  async function getInvoiceLines() {
    try {
      const body = {
        buyer_id: buyer?.id,
      };
      const result = await api.post("invoice-lines", { body: JSON.stringify(body) }).json<any>();
      allInvoiceLines = result;
    } catch (e) {
      console.error(e);
    }
  }

  async function init() {
    await getInvoiceLines();
    await fetchConnectedLines();
  }

  onMount(async () => {
    init();
  });

  $: if (recipes) {
    selectedRecipeIds = recipes
      .map((recipe) => recipe.id)
      .filter((id): id is string => id !== undefined);
  } else {
    selectedRecipeIds = [];
  }

  const getConnectedLines = async () => {
    linkingRecipesToInvoices = true;

    try {
      const body = {
        buyer_id: buyer?.id,
        invoice_lines: [],
        recipe_ids: selectedRecipeIds,
      };
      const result = await api
        .post("link-invoice-recipes", { body: JSON.stringify(body) })
        .json<DatabaseMatch[]>();
      linkingRecipesToInvoices = false;
      return result;
    } catch (e) {
      linkingRecipesToInvoices = false;
      throw e;
    }
  };

  const fetchConnectedLines = async () => {
    let allConnectedLines: RecipeLineMatch[] = [];
    if (linkingRecipesToInvoices) {
      return;
    }

    if (connectedLines.length > 0) {
      return;
    }
    let matchingInvoiceLines: DatabaseMatch[] = [];
    collectIngredientsForAllRecipes();

    matchingInvoiceLines = await getConnectedLines();

    for (const ingredientId in ingredientToRecipes) {
      let ingredient = searchIndex.get(ingredientId);
      let invoiceLines = matchingInvoiceLines.find(
        (line) => line.recipe_ingredient_item_name === ingredient?.name
      )?.invoice_lines;

      let fullInvoiceLines: InvoiceLine[] = [];
      if (invoiceLines) {
        for (const line of invoiceLines) {
          let fullLine = allInvoiceLines.find(
            (fullLine) => fullLine.invoice_line_item_name === line
          );
          if (fullLine) {
            fullInvoiceLines.push(fullLine);
          }
        }
      }

      allConnectedLines.push(<RecipeLineMatch>{
        recipe_ingredient_item_name: ingredient?.name ?? "No ingredient name",
        recipe_ingredient_item_id: ingredient?.id ?? "No ingredient id",
        invoice_lines: fullInvoiceLines,
      });
    }
    matchingInvoiceLines.forEach((ingredient) => {
      if (
        allConnectedLines.find(
          (line) => line.recipe_ingredient_item_name === ingredient.recipe_ingredient_item_name
        )
      ) {
        return;
      }
      if (ingredient.invoice_lines.length === 0) return;
      let fullInvoiceLines: InvoiceLine[] = [];
      for (const line of ingredient.invoice_lines) {
        if (trim(line) === "") continue;
        let fullLine = allInvoiceLines.find((fullLine) => fullLine.invoice_line_item_name === line);
        if (fullLine) {
          fullInvoiceLines.push(fullLine);
        }
      }
      if (fullInvoiceLines.length === 0) return;
      allConnectedLines.push(<RecipeLineMatch>{
        recipe_ingredient_item_name: ingredient.recipe_ingredient_item_name,
        invoice_lines: fullInvoiceLines,
      });
    });
    connectedLines = allConnectedLines;
  };

  const collectIngredientsForAllRecipes = () => {
    ingredientToRecipes = {};
    recipeFlatIngredients = {};

    for (const recipe of recipes) {
      if (!recipe.id) continue;
      if (!recipeFlatIngredients[recipe.id])
        recipeFlatIngredients[recipe.id] = {
          ...recipe,
          items: [],
          type: "recipe",
        };
      recipeFlatIngredients[recipe.id] = {
        ...recipe,
        items: collectIngredients(recipe, recipe.id),
      };
    }
  };

  const saveLinking = async () => {
    savingLinks = true;
    let updatedLines = [...connectedLines];
    connectedLinesManual.forEach((line) => {
      let index = updatedLines.findIndex(
        (updatedLine) =>
          updatedLine.recipe_ingredient_item_name === line.recipe_ingredient_item_name
      );
      if (index >= 0) {
        updatedLines[index] = line;
      } else {
        updatedLines.push(line);
      }
    });

    let payload = {
      buyer_id: buyer?.id,
      links: updatedLines.map((line) => {
        return {
          recipe_ingredient_item_name: line.recipe_ingredient_item_name,
          invoice_lines: line.invoice_lines.map((line) => line.invoice_line_item_name),
        };
      }),
    };
    try {
      await api.post("update-invoice-links", { body: JSON.stringify(payload) });
      connectedLines = JSON.parse(JSON.stringify(connectedLinesManual));
      dispatch("relink");
      savingLinks = false;
    } catch (e) {
      savingLinks = false;
      console.error(e);
    }
  };

  $: connectedLinesManual = JSON.parse(JSON.stringify(connectedLines));
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
  class="modal"
  on:click={(e) => {
    if (e.target === e.currentTarget) {
      dispatch("close");
    }
  }}
>
  <div class="modal-content">
    <div class="modal-header">
      <h1>linking ingredients to invoices</h1>
      <button on:click={() => dispatch("close")}>&times;</button>
    </div>
    <div class="modal-body">
      <div class="accordion">
        <div class="accordion-step open">
          <header>
            <h2>
              <span>recipe:</span>
              {recipes.map((r) => r.name).join(", ")}
              <small>please link your ingredients to their corresponding invoice lines.</small>
            </h2>
            {#if JSON.stringify(connectedLines) !== JSON.stringify(connectedLinesManual)}
              <ButtonSmall size="tiny" on:click={() => saveLinking()} disabled={savingLinks}>
                {#if savingLinks}
                  <LoadingSpinner size={16} />
                {:else}
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    style="filter:invert(1)"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    ><g id="SVGRepo_bgCarrier" stroke-width="0" /><g
                      id="SVGRepo_tracerCarrier"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    /><g id="SVGRepo_iconCarrier">
                      <path
                        d="M4 12.6111L8.92308 17.5L20 6.5"
                        stroke="#000000"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </g>
                  </svg>
                {/if}
              </ButtonSmall>
            {/if}
          </header>
          <div class="accordion-body">
            <div class="recipe-ingredient-invoice-line-links">
              {#if linkingRecipesToInvoices}
                <div class="link-sales-data">
                  <LoadingSpinner size={16} />
                </div>
              {/if}
              {#each connectedLines as line, index}
                {#if line.recipe_ingredient_item_id}
                  <RecipeIngredientLink
                    connectedLines={connectedLinesManual}
                    {index}
                    connectedRecipes={allIngredientToRecipes[line.recipe_ingredient_item_name]
                      .filter((recipe) => !selectedRecipeIds.includes(recipe.id ?? ""))
                      .map((id) => id)}
                    {allInvoiceLines}
                    on:updateConnectedLines={(e) => {
                      connectedLinesManual = e.detail;
                    }}
                  />
                {/if}
              {/each}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<style>
  .modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: flex-end;
    z-index: 9999;
    padding: 20px;
    box-sizing: border-box;
  }

  .modal-content {
    width: 50%;
    background-color: var(--rekki-neutral-01);
    padding: 24px 32px;
    border-radius: 16px;
    display: flex;
    flex-direction: column;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }

  .modal-header h1 {
    margin: 0;
  }

  .modal-header button {
    background-color: transparent;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
  }

  .modal-body {
    margin-bottom: 20px;
    overflow: auto;
    flex: 1;
  }

  .accordion {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .accordion-step {
    background-color: #fff;
    border-radius: 16px;
  }
  .accordion-body {
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .accordion-body.closed {
    display: none;
  }

  .link-sales-data {
    padding: 10px;
    border-radius: 8px;
    background-color: var(--rekki-neutral-02);
    font-size: 14px;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  @keyframes pulse {
    0% {
      opacity: 0.2;
    }
    80% {
      opacity: 1;
    }
    100% {
      opacity: 0.2;
    }
  }
  .accordion-step header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
  }
  .accordion-step.open header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }
  .accordion-step header:last-child {
    border-bottom: none;
  }

  .accordion-step header button {
    background-color: transparent;
    border: none;
    font-size: 1rem;
    cursor: pointer;
  }

  .invoice-item {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 10px 0;
  }
  .recipe-item {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 10px 0;
  }

  .recipe-name {
    flex: 1;
  }

  .recipe-quantity {
    font-size: 16px;
    text-align: right;
  }

  .recipe-ingredient-invoice-line-links {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .ingredient-row {
    opacity: 0.6;
  }
  h2 span {
    font-weight: 100;
  }
  h2 small {
    font-size: 14px;
    font-weight: normal;
    display: block;
  }
</style>
